import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { useTranslation } from 'react-i18next';
import JobService from '../../services/JobService';
import QuoteService from '../../services/QuoteService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { shrinkImage } from '../../utils/image';
import { fmtBytes, fmtDate } from '../../utils/format';

/**
 * Files belong to an owner: a job id (number) or a quote as the string "quote:<id>". A string keeps the
 * value stable across renders, so it can sit in effect dependencies.
 */
export const quoteOwner = (id) => `quote:${id}`;
const quoteIdOf = (owner) => (typeof owner === 'string' && owner.startsWith('quote:') ? Number(owner.slice(6)) : null);
const fetchOwnerFile = (owner, fileId) => {
    const q = quoteIdOf(owner);
    return q ? QuoteService.fetchFile(q, fileId) : JobService.fetchFile(owner, fileId);
};
const uploadOwnerFile = (owner, file, target, onProgress) => {
    const q = quoteIdOf(owner);
    return q ? QuoteService.uploadFile(q, file, onProgress) : JobService.uploadFile(owner, file, target, onProgress);
};

const MAX_BYTES = 100 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
// AutoCAD drawings and Excel/Word files are recognised by extension here (no reliable browser MIME type); the server checks content.
const DOWNLOAD_ONLY_EXT = ['.dwg', '.dxf', '.xlsx', '.xlsm', '.xls', '.docx', '.doc'];
export const ACCEPT_ANY = 'application/pdf,.pdf,image/jpeg,image/png,image/webp,.dwg,.dxf,.xlsx,.xlsm,.xls,.docx,.doc';
export const ACCEPT_PDF = 'application/pdf,.pdf';
export const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp';

/** Blob URL for a stored file (the endpoint needs the auth header, so a plain src would not work). */
export function useBlobUrl(owner, file) {
    const [state, setState] = useState({ url: null, error: false });
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        if (!file) return undefined;
        let url = null;
        let alive = true;
        setState({ url: null, error: false });
        fetchOwnerFile(owner, file.id)
            .then(blob => {
                if (!alive) return;
                url = URL.createObjectURL(new Blob([blob], { type: file.contentType }));
                setState({ url, error: false });
            })
            .catch(() => alive && setState({ url: null, error: true }));
        return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [owner, file?.id, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
    return { ...state, reload: () => setReloadKey(k => k + 1) };
}

const saveBlob = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
};

/**
 * Uploads one or more files (PDFs or photos) to a job or quote, for a job optionally for a step or a visit. Photos are
 * shrunk first. Calls `onDone()` once after the batch so the caller can reload the job and files.
 */
/** Whether the server will take this file type (it checks the content again). */
export const isAcceptedFile = (f) => ACCEPTED.includes(f.type) || f.name.toLowerCase().endsWith('.pdf')
    || DOWNLOAD_ONLY_EXT.some(ext => f.name.toLowerCase().endsWith(ext));

export function useUploader(owner, onDone) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [progress, setProgress] = useState(null);

    const upload = useCallback(async (fileList, target = {}) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const bad = files.find(f => !isAcceptedFile(f));
        if (bad) { showToast(t('files.unsupported'), 'error'); return; }
        let done = 0;
        setProgress(0);
        try {
            for (const original of files) {
                const file = await shrinkImage(original);
                if (file.size > MAX_BYTES) { showToast(t('files.tooLarge'), 'error'); continue; }
                const before = done;
                await uploadOwnerFile(owner, file, target,
                    (p) => setProgress(Math.round(((before + p / 100) / files.length) * 100)));
                done += 1;
            }
            if (done) showToast(t('files.uploaded', { count: done }), 'success');
        } catch (err) {
            showToast(errorMessage(err, t('files.uploadFailed')), 'error');
        } finally {
            setProgress(null);
            if (done) onDone?.();
        }
    }, [owner, onDone, showToast, t]);

    return { upload, progress, uploading: progress !== null };
}

/** Hidden file input plus a function to open it; `capture` opens the phone camera directly. */
export function useFilePicker(onFiles, { accept = ACCEPT_ANY, multiple = true, capture = false } = {}) {
    const ref = useRef(null);
    const input = (
        <input ref={ref} type="file" hidden accept={accept} multiple={multiple}
               {...(capture ? { capture: 'environment' } : {})}
               onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
    );
    return [input, () => ref.current?.click()];
}

/**
 * First page of a job's PDF as a thumbnail (rendered by the server), showing the top of the page; clicking opens
 * the PDF. Renders `fallback` when no preview can be made (e.g. a damaged PDF).
 */
export function PdfPreview({ jobId, file, onOpen, height = 200, fallback = null }) {
    const { t } = useTranslation();
    const [state, setState] = useState({ url: null, error: false });
    useEffect(() => {
        let url = null;
        let alive = true;
        setState({ url: null, error: false });
        JobService.filePreview(jobId, file.id)
            .then(blob => { if (!alive) return; url = URL.createObjectURL(blob); setState({ url, error: false }); })
            .catch(() => alive && setState({ url: null, error: true }));
        return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [jobId, file.id]);
    if (state.error) return fallback;
    return (
        <Box onClick={onOpen} role="button" aria-label={file.name} title={file.name} sx={{
            height, borderRadius: '10px', border: '1px solid #E2E8F0', overflow: 'hidden', cursor: 'zoom-in', bgcolor: '#F8FAFC',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'box-shadow 0.15s',
            '&:hover': { boxShadow: '0 8px 20px -12px rgba(30,58,82,0.45)' },
        }}>
            {state.url
                ? <Box component="img" src={state.url} alt={t('files.previewOf', { name: file.name })}
                       sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                : <CircularProgress size={22} />}
        </Box>
    );
}

/** Square photo thumbnail that opens the viewer. */
export function PhotoThumb({ owner, file, onOpen, size = 96 }) {
    const { url, error } = useBlobUrl(owner, file);
    return (
        <Box onClick={onOpen} title={file.name} sx={{
            width: size, height: size, borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
            bgcolor: '#F1F5F9', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { borderColor: '#427898' },
        }}>
            {error ? <BrokenImageOutlinedIcon sx={{ color: '#CBD5E1' }} />
                : url ? <img src={url} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <CircularProgress size={18} />}
        </Box>
    );
}

/**
 * One document line (PDF, DWG/DXF drawing, Excel sheet or Word document): name, size/date and actions. `label` shows what the file
 * is for (step or visit). Drawings and Office files can't be previewed, so clicking one downloads it.
 */
export function PdfRow({ owner, file, label, onOpen, onDelete, dense }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const drawing = file.kind === 'DRAWING';
    const excel = file.kind === 'EXCEL';
    const word = file.kind === 'WORD';
    const download = async () => {
        setBusy(true);
        try {
            const blob = await fetchOwnerFile(owner, file.id);
            const url = URL.createObjectURL(blob);
            saveBlob(url, file.name);
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } finally {
            setBusy(false);
        }
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: dense ? 0.5 : 1, minWidth: 0 }}>
            <Box sx={{
                width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: word ? 'rgba(43,87,154,0.10)' : excel ? 'rgba(16,124,65,0.10)' : drawing ? 'rgba(66,120,152,0.10)' : 'rgba(239,68,68,0.08)',
                color: word ? '#2B579A' : excel ? '#107C41' : drawing ? '#427898' : '#EF4444',
            }}>
                {word ? <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                    : excel ? <TableChartOutlinedIcon sx={{ fontSize: 18 }} />
                    : drawing ? <ArchitectureOutlinedIcon sx={{ fontSize: 18 }} /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={drawing || excel || word ? download : onOpen}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: '#1a2332', '&:hover': { color: '#427898' } }}>{file.name}</Typography>
                <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>
                    {label ? `${label} · ` : ''}{fmtBytes(file.size)} · {fmtDate(file.uploadedAt)}
                </Typography>
            </Box>
            <Tooltip title={t('files.download')}><span><IconButton size="small" disabled={busy} onClick={download}><FileDownloadOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>
            {onDelete && <Tooltip title={t('common.delete')}><IconButton size="small" onClick={onDelete} sx={{ '&:hover': { color: '#EF4444' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>}
        </Box>
    );
}

/** Full-screen viewer for one file of a list: photo or PDF, with previous/next for photos. */
export function FileViewer({ owner, files, index, onIndex, onClose, onDelete }) {
    const { t } = useTranslation();
    const file = index != null ? files[index] : null;
    const { url, error, reload } = useBlobUrl(owner, file);
    const isPdf = file?.kind === 'PDF';
    const go = (d) => onIndex((index + d + files.length) % files.length);

    useEffect(() => {
        if (!file || isPdf || files.length < 2) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') go(-1);
            if (e.key === 'ArrowRight') go(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Dialog open={!!file} onClose={onClose} fullScreen
                PaperProps={{ sx: { bgcolor: isPdf ? '#F1F5F9' : 'rgba(15,23,42,0.96)' } }}>
            {file && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 700, fontSize: 14 }}>{file.name}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                                {fmtBytes(file.size)} · {fmtDate(file.uploadedAt)}{!isPdf && files.length > 1 ? ` · ${index + 1} / ${files.length}` : ''}
                            </Typography>
                        </Box>
                        <Tooltip title={t('files.openNewTab')}><span><IconButton disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}><OpenInNewIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title={t('files.download')}><span><IconButton disabled={!url} onClick={() => saveBlob(url, file.name)}><FileDownloadOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>
                        {onDelete && <Tooltip title={t('common.delete')}><IconButton onClick={() => onDelete(file)} sx={{ '&:hover': { color: '#EF4444' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>}
                        <IconButton onClick={onClose} aria-label={t('common.close')}><CloseIcon /></IconButton>
                    </Box>
                    <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                        {error ? (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: isPdf ? '#64748B' : '#CBD5E1', mb: 1.5 }}>{t('files.loadFailed')}</Typography>
                                <Button variant="outlined" onClick={reload}>{t('common.retry')}</Button>
                            </Box>
                        ) : !url ? <CircularProgress />
                            : isPdf ? <iframe title={file.name} src={url} style={{ width: '100%', height: '100%', border: 0 }} />
                                : <img src={url} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                        {!isPdf && files.length > 1 && (
                            <>
                                <IconButton onClick={() => go(-1)} aria-label={t('files.previous')} sx={{ position: 'absolute', left: 12, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' } }}><ChevronLeftIcon /></IconButton>
                                <IconButton onClick={() => go(1)} aria-label={t('files.next')} sx={{ position: 'absolute', right: 12, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' } }}><ChevronRightIcon /></IconButton>
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Dialog>
    );
}
